import { forwardRef } from 'react';
import type { ChangeEvent, DetailedHTMLProps } from 'react';

import { cn } from '@wwe-portal/ui/lib/lib';
import { IcoMoonIcon } from '../icons';

type Props = DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement | null>,
  HTMLDivElement
> & {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  value: string | null;
  hideIcon?: boolean;
  className?: string;
  icon?: React.ReactNode;
  postIcon?: React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.RefObject<HTMLInputElement | null>;
};

export const Input = forwardRef<HTMLDivElement, Props>(
  (
    {
      placeholder,
      onChange,
      value,
      tabIndex,
      hideIcon,
      icon,
      postIcon,
      className,
      inputProps,
      inputRef,
      ...rest
    }: Props,
    ref
  ) => {
    return (
      <div className="relative" ref={ref} {...rest}>
        {!hideIcon || icon ? (
          <span className="absolute inset-y-0 left-0 z-10 flex items-center pl-2.5">
            <span className="p-1 focus:outline-none">
              {icon ?? (
                <IcoMoonIcon
                  iconName="search-2"
                  className="h-4 w-4 text-[#B4BAE9]"
                />
              )}
            </span>
          </span>
        ) : null}
        <input
          autoComplete="off"
          className={cn(
            'bg-back300 border-back300 placeholder-text700 focus:bg-back400 h-[40px] w-full rounded-[10px] border-[1px] pl-10 text-base text-[#B4BAE9] placeholder:text-sm placeholder:italic focus:outline-none focus-visible:outline-none',
            { 'pr-10': postIcon },
            className
          )}
          onChange={onChange}
          placeholder={placeholder}
          tabIndex={tabIndex}
          value={value ?? ''}
          ref={inputRef}
          {...inputProps}
        />
        {postIcon ? (
          <span className="absolute inset-y-0 right-0 flex items-center pr-2.5">
            {postIcon}
          </span>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
